import { Field, FieldDataType, FieldType } from 'module/api/field/types';
import { unreachable } from 'module/helpers/unreachable';
import {
  Boolean,
  Calendar,
  Hashtag,
  ListChecked,
  RadioButtonChecked,
  StringText,
  TextAlignJustify,
  Upload,
  User,
  UserMultiple,
} from '@carbon/icons-react';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UiFieldType } from './types';

export const getUiFieldType = (
  field: Pick<Field, 'fieldDataType' | 'fieldType'> &
    Partial<Pick<Field, 'optionsAvailable'>>,
): UiFieldType => {
  const { optionsAvailable, fieldDataType, fieldType } = field;
  const isMulti = fieldType === FieldType.MultiSelect;

  switch (fieldDataType) {
    case FieldDataType.Boolean:
      return UiFieldType.Boolean;
    case FieldDataType.MultiLineText:
      return UiFieldType.MultiLineText;
    case FieldDataType.Text:
      return optionsAvailable
        ? isMulti
          ? UiFieldType.MultiSelect
          : UiFieldType.SingleSelect
        : UiFieldType.Text;
    case FieldDataType.Number:
      return UiFieldType.Number;
    case FieldDataType.Date:
      return UiFieldType.Date;
    case FieldDataType.User:
      return isMulti
        ? UiFieldType.MultiUserSelect
        : UiFieldType.SingleUserSelect;
    case FieldDataType.Note:
      return unreachable(fieldDataType as never);
    case FieldDataType.Upload:
      return UiFieldType.Upload;
    default:
      return unreachable(fieldDataType);
  }
};
export const getFieldIcon = (type: UiFieldType): React.JSX.Element => {
  switch (type) {
    case UiFieldType.Boolean:
      return <Boolean size={16} />;
    case UiFieldType.Date:
      return <Calendar size={16} />;
    case UiFieldType.Number:
      return <Hashtag size={16} />;
    case UiFieldType.MultiSelect:
      return <ListChecked size={16} />;
    case UiFieldType.MultiUserSelect:
      return <UserMultiple size={16} />;
    case UiFieldType.SingleSelect:
      return <RadioButtonChecked size={16} />;
    case UiFieldType.SingleUserSelect:
      return <User size={16} />;
    case UiFieldType.Text:
      return <StringText size={16} />;
    case UiFieldType.MultiLineText:
      return <TextAlignJustify size={16} />;
    case UiFieldType.Upload:
      return <Upload size={16} />;
    default:
      return unreachable(type);
  }
};
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface FieldTypeWithIconProps {
  fieldType: UiFieldType;
}
export const FieldTypeWithIcon: FC<FieldTypeWithIconProps> = ({
  fieldType,
}) => {
  const fieldIcon = getFieldIcon(fieldType);

  return (
    <Wrapper>
      {fieldIcon}
      <FormattedMessage id={`fields.label.${fieldType.toLowerCase()}`} />
    </Wrapper>
  );
};

interface Props {
  field: Field;
}
export const FieldTypeWithIconFromField: FC<Props> = ({ field }) => {
  const fieldType = getUiFieldType(field);

  return <FieldTypeWithIcon fieldType={fieldType} />;
};

export const getFieldTypeFromUiType = (
  type: UiFieldType,
): Pick<Field, 'optionsAvailable' | 'fieldDataType' | 'fieldType'> => {
  switch (type) {
    case UiFieldType.Boolean:
      return {
        fieldDataType: FieldDataType.Boolean,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: false,
      };
    case UiFieldType.MultiLineText:
      return {
        fieldDataType: FieldDataType.MultiLineText,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: false,
      };
    case UiFieldType.Number:
      return {
        fieldDataType: FieldDataType.Number,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: false,
      };
    case UiFieldType.Date:
      return {
        fieldDataType: FieldDataType.Date,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: false,
      };
    case UiFieldType.Text:
      return {
        fieldDataType: FieldDataType.Text,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: false,
      };
    case UiFieldType.SingleSelect:
      return {
        fieldDataType: FieldDataType.Text,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: true,
      };
    case UiFieldType.MultiSelect:
      return {
        fieldDataType: FieldDataType.Text,
        fieldType: FieldType.MultiSelect,
        optionsAvailable: true,
      };
    case UiFieldType.SingleUserSelect:
      return {
        fieldDataType: FieldDataType.User,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: true,
      };
    case UiFieldType.MultiUserSelect:
      return {
        fieldDataType: FieldDataType.User,
        fieldType: FieldType.MultiSelect,
        optionsAvailable: true,
      };
    case UiFieldType.Upload:
      return {
        fieldDataType: FieldDataType.Upload,
        fieldType: FieldType.SingleSelect,
        optionsAvailable: true,
      };

    default:
      return unreachable(type);
  }
};
